import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { HOURS, formatHour, formatINR } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, X, Info, QrCode, Copy, CheckCircle2 } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";

export default function SlotMatrix() {
  const [params, setParams] = useSearchParams();
  const initialDate = params.get("date") || new Date().toISOString().slice(0, 10);
  const highlightId = params.get("highlight");
  const initialFacility = params.get("facility") || "all";
  const [date, setDate] = useState(initialDate);
  const [matrix, setMatrix] = useState(null);
  const [services, setServices] = useState([]);
  const [showManual, setShowManual] = useState(false);
  const [manual, setManual] = useState({
    service_slug: "indoor-box", resource: "Box A", date, start_hour: 10, duration: 1,
    guest_name: "", guest_phone: "", payment_method: "cash",
    discount: 0, discount_reason: "",
    ball_type: "", overs: 0, fixed_slot_id: "",
  });

  const load = () => api.get("/admin/slot-matrix", { params: { date } }).then((r) => setMatrix(r.data));
  useEffect(() => {
    load();
    api.get("/services").then((r) => setServices(r.data.filter((s) => s.type === "hourly")));
  }, [date]);

  const cancel = async (bookingId) => {
    if (!confirm("Cancel this booking?")) return;
    await api.delete(`/admin/bookings/${bookingId}`);
    toast.success("Cancelled");
    load();
  };

  const openBookForCell = (row, hour) => {
    // Prefill modal with the clicked slot so admin can book it in one step.
    setManual((m) => ({
      ...m,
      service_slug: row.slug,
      resource: row.resource,
      date,
      start_hour: hour,
      duration: 1,
    }));
    setShowManual(true);
  };

  const createManual = async () => {
    try {
      const { data } = await api.post("/admin/manual-booking", { ...manual, date });
      if (data.phonepe_redirect_url) {
        // Payment Gateway flow — show a QR the customer can scan without
        // handing over the tablet. The booking sits in `payment_status=pending`
        // and auto-flips to `paid` on PhonePe webhook / status poll.
        setPendingCheckout({
          url: data.phonepe_redirect_url,
          bookingId: data.id || data._id,
          orderId: data.phonepe_merchant_order_id,
          total: previewPrice.total,
          guest: manual.guest_name || "Walk-in",
        });
      } else {
        toast.success("Booking confirmed");
        setShowManual(false);
        load();
      }
    } catch (e) { toast.error(apiError(e)); }
  };
  const [pendingCheckout, setPendingCheckout] = useState(null);
  const [payStatus, setPayStatus] = useState("waiting");
  const [chaseTarget, setChaseTarget] = useState(null);
  const [chasing, setChasing] = useState(false);

  const doChase = async () => {
    if (!chaseTarget) return;
    setChasing(true);
    try {
      const { data } = await api.post(`/admin/booking-chase-payment/${chaseTarget.id}`);
      toast.success(`WhatsApp sent — customer will get a fresh payment link (${data.whatsapp_status || "queued"}).`);
      setChaseTarget(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setChasing(false); }
  };

  // Poll booking payment_status while the QR is on-screen so the modal
  // flips to "Paid" the moment the customer completes the UPI payment.
  useEffect(() => {
    if (!pendingCheckout?.bookingId) return;
    let timer = null;
    let cancelled = false;
    const tick = async () => {
      try {
        // Piggyback on /finalize which polls PhonePe and updates our booking.
        // The txn_id is the payment_transactions row id, which we don't have
        // client-side. Instead, refetch the slot-matrix and check the cell.
        const { data } = await api.get("/admin/slot-matrix", { params: { date } });
        const row = (data.rows || []).find(r => r.slug === manual.service_slug && r.resource === manual.resource);
        const cell = row?.hours?.[manual.start_hour];
        if (cell && cell.id) {
          // Cell is now booked with our booking → payment confirmed by webhook
          if (!cancelled) { setPayStatus("paid"); toast.success("Payment received!"); }
          return;
        }
      } catch (_) { /* transient; keep polling */ }
      if (!cancelled) timer = setTimeout(tick, 4000);
    };
    timer = setTimeout(tick, 4000);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [pendingCheckout, date, manual.service_slug, manual.resource, manual.start_hour]);

  const closeCheckout = () => {
    setPendingCheckout(null);
    setPayStatus("waiting");
    setShowManual(false);
    load();
  };

  // Slot stats for the header KPIs
  const stats = useMemo(() => {
    if (!matrix) return { total: 0, booked: 0 };
    let total = 0, booked = 0;
    matrix.rows.forEach((row) => {
      matrix.hours.forEach((h) => {
        total += 1;
        if (row.hours[h]) booked += 1;
      });
    });
    return { total, booked, free: total - booked };
  }, [matrix]);

  const selectedSvc = services.find((s) => s.slug === manual.service_slug);
  // Derive effective booking mode from data availability — safer than trusting
  // a single field that admins may have accidentally reset via a form save.
  const bookingMode = (selectedSvc?.ball_pricing?.length > 0)
    ? "overs"
    : (selectedSvc?.fixed_slots?.length > 0 ? "fixed_slots" : (selectedSvc?.booking_mode || "hourly"));
  const previewHours = Math.max(1, parseInt(manual.duration) || 1);
  const [previewPrice, setPreviewPrice] = useState({ amount: 0, tax_amount: 0, total: 0, gst_percent: 0, breakdown: [], rule_summary: null });
  const [pricingLoading, setPricingLoading] = useState(false);

  // Reset mode-specific fields when service changes
  useEffect(() => {
    setManual((m) => ({ ...m, ball_type: "", overs: 0, fixed_slot_id: "" }));
  }, [manual.service_slug]);

  // Fetch authoritative price whenever inputs change
  useEffect(() => {
    if (!showManual || !manual.service_slug) return;
    let cancelled = false;
    setPricingLoading(true);
    const params = {
      service_slug: manual.service_slug,
      date,
      start_hour: manual.start_hour,
      duration: previewHours,
      discount: manual.discount || 0,
    };
    if (bookingMode === "overs") {
      params.ball_type = manual.ball_type || "";
      params.overs = manual.overs || 0;
    } else if (bookingMode === "fixed_slots") {
      params.fixed_slot_id = manual.fixed_slot_id || "";
    }
    api.get("/admin/booking-price", { params })
      .then((r) => { if (!cancelled) setPreviewPrice(r.data); })
      .catch(() => { if (!cancelled) setPreviewPrice({ amount: 0, tax_amount: 0, total: 0, gst_percent: 0, breakdown: [], rule_summary: null }); })
      .finally(() => { if (!cancelled) setPricingLoading(false); });
    return () => { cancelled = true; };
  }, [showManual, manual.service_slug, manual.start_hour, previewHours, date, manual.discount, manual.ball_type, manual.overs, manual.fixed_slot_id, bookingMode]);

  // Facility tab filter — "all" shows the unified grid.
  const [activeFacility, setActiveFacility] = useState(initialFacility);
  // Persist tab choice in URL so refresh / share keeps context.
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (activeFacility === "all") next.delete("facility"); else next.set("facility", activeFacility);
    // Only update if something actually changed to avoid a history spam loop.
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFacility]);
  const facilities = useMemo(() => {
    if (!matrix?.rows) return [];
    const seen = new Map();
    matrix.rows.forEach((r) => { if (!seen.has(r.slug)) seen.set(r.slug, r.service); });
    return Array.from(seen.entries()).map(([slug, name]) => ({ slug, name }));
  }, [matrix]);
  const filteredRows = useMemo(() => {
    if (!matrix?.rows) return [];
    if (activeFacility === "all") return matrix.rows;
    return matrix.rows.filter((r) => r.slug === activeFacility);
  }, [matrix, activeFacility]);

  // Click-drag range selection on free cells (Google-Cal-style).
  const [dragRange, setDragRange] = useState(null); // {slug, resource, startHour, endHour}
  const [dragCursor, setDragCursor] = useState(null); // {x, y} — floating preview chip
  const justCommittedRef = useRef(false); // suppress the click that fires right after a drag mouseup
  const isCellInDrag = (row, h) => {
    if (!dragRange || dragRange.slug !== row.slug || dragRange.resource !== row.resource) return false;
    const lo = Math.min(dragRange.startHour, dragRange.endHour);
    const hi = Math.max(dragRange.startHour, dragRange.endHour);
    return h >= lo && h <= hi;
  };

  // Live preview during drag: hours in selection + sum of hourly prices from row.price_map.
  const dragPreview = useMemo(() => {
    if (!dragRange || !matrix?.rows) return null;
    const row = matrix.rows.find((r) => r.slug === dragRange.slug && r.resource === dragRange.resource);
    if (!row) return null;
    const lo = Math.min(dragRange.startHour, dragRange.endHour);
    const hi = Math.max(dragRange.startHour, dragRange.endHour);
    let total = 0;
    let blocked = false;
    for (let h = lo; h <= hi; h++) {
      if (row.hours[h]) { blocked = true; break; }
      total += Math.round(row?.price_map?.[h] || 0);
    }
    return { hours: hi - lo + 1, total, blocked, resource: row.resource };
  }, [dragRange, matrix]);
  const commitDrag = () => {
    if (!dragRange) return;
    const row = matrix.rows.find((r) => r.slug === dragRange.slug && r.resource === dragRange.resource);
    if (!row) { setDragRange(null); setDragCursor(null); return; }
    const lo = Math.min(dragRange.startHour, dragRange.endHour);
    const hi = Math.max(dragRange.startHour, dragRange.endHour);
    // Only commit if all cells in range are free (no bookings on top)
    for (let h = lo; h <= hi; h++) {
      if (row.hours[h]) { setDragRange(null); setDragCursor(null); return; }
    }
    setManual({
      service_slug: row.slug, resource: row.resource, date,
      start_hour: lo, duration: hi - lo + 1,
      guest_name: "", guest_phone: "", payment_method: "cash",
      discount: 0, discount_reason: "", ball_type: "", overs: 0, fixed_slot_id: "",
    });
    setShowManual(true);
    setDragRange(null);
    setDragCursor(null);
    justCommittedRef.current = true;
    // Clear the guard on the next tick so future single-clicks work.
    setTimeout(() => { justCommittedRef.current = false; }, 0);
  };
  useEffect(() => {
    const onUp = () => { if (dragRange) commitDrag(); };
    const onMove = (e) => { if (dragRange) setDragCursor({ x: e.clientX, y: e.clientY }); };
    window.addEventListener("mouseup", onUp);
    window.addEventListener("mousemove", onMove);
    return () => {
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("mousemove", onMove);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragRange, matrix, date]);

  return (
    <div data-testid="admin-slot-matrix">
      <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Unified Grid · All services</div>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none">Slot Matrix.</h1>
          <p className="text-white/60 text-sm mt-2 max-w-2xl">
            Every hourly service on one grid. Click a <span className="brand-blue">blank cell</span> to book,{" "}
            <span className="brand-blue">drag across cells</span> to book multiple hours, or a{" "}
            <span className="text-red-400">red cell</span> to cancel.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="date" value={date} onChange={(e) => setDate(e.target.value)}
            data-testid="matrix-date"
            className="bg-[#131A2B] border border-white/12 py-2 px-3 text-white text-sm"
          />
          <button onClick={() => { setManual((m) => ({ ...m, date, start_hour: 10, duration: 1 })); setShowManual(true); }} data-testid="new-manual-booking" className="bg-brand-blue text-white px-4 py-2 text-xs uppercase font-bold tracking-widest flex items-center gap-2 hover:glow-blue">
            <Plus size={12} /> Phone-in Booking
          </button>
        </div>
      </div>

      {matrix && (
        <div className="mt-6 grid grid-cols-3 gap-3">
          <Kpi label="Slots visible" value={stats.total} testid="kpi-total" />
          <Kpi label="Booked" value={stats.booked} cls="text-red-400" testid="kpi-booked" />
          <Kpi label="Available" value={stats.free} cls="brand-blue" testid="kpi-free" />
        </div>
      )}

      {matrix && facilities.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-2" data-testid="facility-tabs">
          <button
            type="button"
            onClick={() => setActiveFacility("all")}
            data-testid="facility-tab-all"
            className={`px-4 py-2 font-mono text-xs uppercase tracking-widest border transition ${
              activeFacility === "all"
                ? "border-brand-blue bg-brand-blue/10 brand-blue"
                : "border-white/20 text-white/60 hover:border-white/40"
            }`}
          >
            All · {facilities.length}
          </button>
          {facilities.map((f) => (
            <button
              key={f.slug}
              type="button"
              onClick={() => setActiveFacility(f.slug)}
              data-testid={`facility-tab-${f.slug}`}
              className={`px-4 py-2 font-mono text-xs uppercase tracking-widest border transition ${
                activeFacility === f.slug
                  ? "border-brand-blue bg-brand-blue/10 brand-blue"
                  : "border-white/20 text-white/60 hover:border-white/40"
              }`}
            >
              {f.name}
            </button>
          ))}
        </div>
      )}

      {matrix && (
        <div className="mt-6 overflow-x-auto border border-white/12" onMouseLeave={() => setDragRange(null)}>
          <table className="w-full font-mono text-xs select-none">
            <thead>
              <tr className="bg-[#131A2B]">
                <th className="p-3 text-left text-white/40 uppercase tracking-widest sticky left-0 bg-[#131A2B] w-56">Service / Resource</th>
                {matrix.hours.map((h) => (
                  <th key={h} className="p-2 text-center text-white/40 min-w-[52px]">{formatHour(h)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => (
                <tr key={`${row.slug}-${row.resource}`} className="border-t border-white/12">
                  <td className="p-3 text-white/80 sticky left-0 bg-[#0B1120] border-r border-white/12">
                    <div className="font-bold uppercase">{row.resource}</div>
                    <div className="text-[10px] text-white/40 mt-0.5">{row.service}</div>
                  </td>
                  {matrix.hours.map((h) => {
                    const b = row.hours[h];
                    const bg = b ? (b.checked_in ? "checked-in" : "booked") : "free";
                    const now = Date.now();
                    const created = b?.created_at ? new Date(b.created_at).getTime() : 0;
                    const isStalePending = b && b.payment_status === "pending" && (now - created) > 10 * 60_000;
                    const isPending = b && b.payment_status === "pending";
                    const inDrag = !b && isCellInDrag(row, h);
                    return (
                      <td
                        key={h}
                        data-testid={`matrix-cell-${row.slug}-${row.resource}-${h}`}
                        onMouseDown={(e) => {
                          if (b) return;
                          e.preventDefault();
                          setDragRange({ slug: row.slug, resource: row.resource, startHour: h, endHour: h });
                        }}
                        onMouseEnter={() => {
                          if (b || !dragRange) return;
                          if (dragRange.slug !== row.slug || dragRange.resource !== row.resource) return;
                          setDragRange({ ...dragRange, endHour: h });
                        }}
                        onClick={() => {
                          if (justCommittedRef.current) return; // drag just fired — suppress this click
                          if (!b) return openBookForCell(row, h);
                          if (isPending) return setChaseTarget({ ...b, hour: h, service_slug: row.slug, resource: row.resource });
                          cancel(b.id);
                        }}
                        title={b ? `${b.user}${b.phone ? " · " + b.phone : ""}${b.checked_in ? "\n✓ Checked in" : ""}${isPending ? "\n⏱ PhonePe payment pending — click to chase" : "\nClick to cancel"}` : `Free — click to book, or drag to book multiple hours (${row.resource} @ ${formatHour(h)})`}
                        className={`matrix-cell text-center cursor-pointer text-[9px] leading-tight ${bg} ${b?.checked_in ? "bg-green-500/30 border border-green-500/60" : ""} ${isPending ? "!bg-yellow-500/20 !border !border-yellow-500/60" : ""} ${b && highlightId === b.id ? "ring-2 ring-brand-orange" : ""} ${inDrag ? "!bg-brand-blue/30 !border !border-brand-blue" : ""} relative`}
                      >
                        {b ? (
                          <>
                            {isStalePending && (
                              <span
                                data-testid={`pending-dot-${row.slug}-${row.resource}-${h}`}
                                className="absolute top-0.5 right-0.5 h-2 w-2 rounded-full bg-red-500 animate-pulse"
                                title="Payment pending > 10 mins"
                              />
                            )}
                            <span>{b.checked_in && "✓ "}{isPending && !b.checked_in && "⏱ "}{b.user.slice(0, 6)}</span>
                          </>
                        ) : (
                          <span className="text-white/50">
                            {row.price_map && row.price_map[h] > 0
                              ? `₹${Math.round(row.price_map[h])}`
                              : "+"}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-4 flex items-center gap-4 text-xs font-mono uppercase tracking-widest text-white/50 flex-wrap">
        <span className="flex items-center gap-2"><span className="w-3 h-3 border border-white/20 flex items-center justify-center brand-blue">+</span> Free — click to book</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 bg-red-500/30 border border-red-500/50"></span> Booked — click to cancel</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 bg-green-500/30 border border-green-500/60"></span> ✓ Checked in</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 bg-yellow-500/20 border border-yellow-500/60"></span> Payment pending</span>
        <span className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-red-500 animate-pulse"></span> Pending {">"} 10 min — chase</span>
        <span className="flex items-center gap-2 text-white/40"><Info size={11} /> Prices shown are inclusive of applicable GST</span>
      </div>

      {showManual && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setShowManual(false)}>
          <div className="bg-[#131A2B] border border-white/12 p-8 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-display text-2xl uppercase font-black">Phone-in Booking</h3>
                <p className="text-xs text-white/50 mt-1">
                  {selectedSvc?.name} · {manual.resource} ·{" "}
                  {bookingMode === "overs" && manual.ball_type && manual.overs
                    ? `${((selectedSvc?.ball_pricing || []).find(b => b.slug === manual.ball_type)?.name)} · ${manual.overs} overs`
                    : bookingMode === "fixed_slots" && manual.fixed_slot_id
                    ? ((selectedSvc?.fixed_slots || []).find(s => (s.id || s.slug || s.label) === manual.fixed_slot_id)?.label || manual.fixed_slot_id)
                    : `${formatHour(manual.start_hour)} · ${previewHours}h`}
                </p>
              </div>
              <button onClick={() => setShowManual(false)}><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <select value={manual.service_slug} onChange={(e) => setManual({ ...manual, service_slug: e.target.value, resource: services.find(s => s.slug === e.target.value)?.resources[0] || "" })} data-testid="manual-service" className="bg-transparent border border-white/20 py-3 px-3 col-span-2">
                {services.map((s) => <option key={s.slug} value={s.slug} className="bg-[#131A2B]">{s.name}</option>)}
              </select>
              <select value={manual.resource} onChange={(e) => setManual({ ...manual, resource: e.target.value })} data-testid="manual-resource" className="bg-transparent border border-white/20 py-3 px-3 col-span-2">
                {selectedSvc?.resources.map((r) => <option key={r} value={r} className="bg-[#131A2B]">{r}</option>)}
              </select>

              {bookingMode === "hourly" && (
                <>
                  <div className="relative">
                    <label className="absolute -top-2 left-2 bg-[#131A2B] px-1 text-[9px] font-mono uppercase tracking-widest text-white/50">Start hour (24h)</label>
                    <input type="number" min="6" max="22" value={manual.start_hour} onChange={(e) => setManual({ ...manual, start_hour: parseInt(e.target.value) })} data-testid="manual-hour" className="w-full bg-transparent border border-white/20 py-3 px-3" />
                  </div>
                  <div className="relative">
                    <label className="absolute -top-2 left-2 bg-[#131A2B] px-1 text-[9px] font-mono uppercase tracking-widest text-white/50">Duration (hours)</label>
                    <input type="number" min="1" max="6" value={manual.duration} onChange={(e) => setManual({ ...manual, duration: parseInt(e.target.value) })} data-testid="manual-duration" className="w-full bg-transparent border border-white/20 py-3 px-3" />
                  </div>
                </>
              )}

              {bookingMode === "overs" && (
                <>
                  <div className="relative">
                    <label className="absolute -top-2 left-2 bg-[#131A2B] px-1 text-[9px] font-mono uppercase tracking-widest text-white/50">Start hour (24h)</label>
                    <input type="number" min="6" max="22" value={manual.start_hour} onChange={(e) => setManual({ ...manual, start_hour: parseInt(e.target.value) })} data-testid="manual-hour" className="w-full bg-transparent border border-white/20 py-3 px-3" />
                  </div>
                  <div className="relative">
                    <label className="absolute -top-2 left-2 bg-[#131A2B] px-1 text-[9px] font-mono uppercase tracking-widest text-white/50">Ball type</label>
                    <select value={manual.ball_type} onChange={(e) => setManual({ ...manual, ball_type: e.target.value, overs: 0 })} data-testid="manual-ball-type" className="w-full bg-transparent border border-white/20 py-3 px-3">
                      <option value="" className="bg-[#131A2B]">Select ball…</option>
                      {(selectedSvc?.ball_pricing || []).map((b) => (
                        <option key={b.slug} value={b.slug} className="bg-[#131A2B]">{b.name}</option>
                      ))}
                    </select>
                  </div>
                  {manual.ball_type && (
                    <div className="col-span-2" data-testid="manual-overs-tiers">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-2">Overs tier</div>
                      <div className="flex flex-wrap gap-2">
                        {((selectedSvc?.ball_pricing || []).find((b) => b.slug === manual.ball_type)?.tiers || []).map((t) => (
                          <button
                            key={t.overs}
                            type="button"
                            onClick={() => setManual({ ...manual, overs: t.overs })}
                            data-testid={`manual-overs-${t.overs}`}
                            className={`px-4 py-2 font-mono uppercase tracking-widest text-xs border transition-colors ${
                              manual.overs === t.overs
                                ? "border-brand-orange brand-orange bg-brand-orange/10"
                                : "border-white/20 text-white/70 hover:border-white/40"
                            }`}
                          >
                            {t.overs} overs · ₹{t.price}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {bookingMode === "fixed_slots" && (
                <div className="col-span-2">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-2">Fixed slot</div>
                  <div className="flex flex-wrap gap-2" data-testid="manual-fixed-slots">
                    {(selectedSvc?.fixed_slots || []).map((s) => {
                      const id = s.id || s.slug || s.label;
                      const on = manual.fixed_slot_id === id;
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setManual({ ...manual, fixed_slot_id: id, start_hour: s.start_hour || 0, duration: s.duration_hours || s.duration || 1 })}
                          data-testid={`manual-slot-${id}`}
                          className={`text-left px-4 py-2 font-mono text-xs border transition-colors ${
                            on ? "border-brand-orange brand-orange bg-brand-orange/10" : "border-white/20 text-white/70 hover:border-white/40"
                          }`}
                        >
                          <div className="uppercase tracking-widest">{s.label || `${s.start_hour}:00`}</div>
                          <div className="opacity-70 mt-0.5">{s.duration_hours || s.duration || 1}h · ₹{s.price}</div>
                        </button>
                      );
                    })}
                    {(selectedSvc?.fixed_slots || []).length === 0 && (
                      <div className="text-white/40 text-xs">
                        No fixed slots configured for this service. Add them in Admin → Slot Booking Prices.
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="relative">
                <label className="absolute -top-2 left-2 bg-[#131A2B] px-1 text-[9px] font-mono uppercase tracking-widest text-white/50">Guest name</label>
                <input value={manual.guest_name} onChange={(e) => setManual({ ...manual, guest_name: e.target.value })} data-testid="manual-name" className="w-full bg-transparent border border-white/20 py-3 px-3" />
              </div>
              <div className="relative">
                <label className="absolute -top-2 left-2 bg-[#131A2B] px-1 text-[9px] font-mono uppercase tracking-widest text-white/50">Phone</label>
                <input value={manual.guest_phone} onChange={(e) => setManual({ ...manual, guest_phone: e.target.value })} data-testid="manual-phone" className="w-full bg-transparent border border-white/20 py-3 px-3" />
              </div>
              <div className="relative col-span-2">
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-2">Receive payment via</div>
                <div className="flex flex-wrap gap-2" data-testid="manual-payment-methods">
                  {[
                    { v: "cash",    label: "Cash" },
                    { v: "gateway", label: "Payment Gateway (PhonePe)" },
                  ].map((m) => {
                    const on = manual.payment_method === m.v;
                    return (
                      <button
                        key={m.v}
                        type="button"
                        onClick={() => setManual({ ...manual, payment_method: m.v })}
                        data-testid={`manual-pm-${m.v}`}
                        className={`px-4 py-2 font-mono text-xs uppercase tracking-widest border transition ${
                          on ? "border-brand-blue bg-brand-blue/10 brand-blue"
                             : "border-white/20 text-white/60 hover:border-white/40"
                        }`}
                      >
                        {m.label}
                      </button>
                    );
                  })}
                </div>
                {manual.payment_method === "gateway" && (
                  <div className="text-[10px] text-brand-orange font-mono mt-2">
                    A PhonePe checkout link will open in a new tab — show the QR to the customer or forward the link. Booking auto-confirms on payment success.
                  </div>
                )}
              </div>
              <div className="relative">
                <label className="absolute -top-2 left-2 bg-[#131A2B] px-1 text-[9px] font-mono uppercase tracking-widest text-white/50">Discount (₹)</label>
                <input type="number" min="0" step="1" value={manual.discount} onChange={(e) => setManual({ ...manual, discount: Math.max(0, parseFloat(e.target.value) || 0) })} data-testid="manual-discount" placeholder="0" className="w-full bg-transparent border border-white/20 py-3 px-3" />
              </div>
              <div className="relative">
                <label className="absolute -top-2 left-2 bg-[#131A2B] px-1 text-[9px] font-mono uppercase tracking-widest text-white/50">Reason (optional)</label>
                <input value={manual.discount_reason} onChange={(e) => setManual({ ...manual, discount_reason: e.target.value })} data-testid="manual-discount-reason" placeholder="Regular · Member · Trial" className="w-full bg-transparent border border-white/20 py-3 px-3" />
              </div>
            </div>
            <div className="mt-6 flex items-center justify-between border-t border-white/10 pt-4">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">Total</div>
                <div className="font-display text-2xl font-black brand-blue flex items-center gap-3" data-testid="manual-total">
                  {pricingLoading ? "…" : formatINR(previewPrice.total || 0)}
                  {previewPrice.rule_summary && !pricingLoading && (
                    <span
                      data-testid="manual-rule-chip"
                      className="inline-flex items-center gap-1 px-2 py-1 border border-brand-orange/50 bg-brand-orange/10 brand-orange text-[10px] font-mono uppercase tracking-widest"
                    >
                      {previewPrice.rule_summary.label}
                      {previewPrice.rule_summary.delta > 0 && (
                        <span className="text-green-300">+{formatINR(previewPrice.rule_summary.delta)}</span>
                      )}
                      {previewPrice.rule_summary.delta < 0 && (
                        <span className="text-red-300">−{formatINR(Math.abs(previewPrice.rule_summary.delta))}</span>
                      )}
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-white/40 font-mono mt-1">
                  {previewPrice.discount > 0 && (
                    <>Gross {formatINR(previewPrice.gross_amount)} − Disc {formatINR(previewPrice.discount)}{previewPrice.gst_percent > 0 ? ` + ${previewPrice.gst_percent}% GST` : ""}</>
                  )}
                  {!previewPrice.discount && (previewPrice.gst_percent > 0
                    ? `Base ${formatINR(previewPrice.amount || 0)} + ${previewPrice.gst_percent}% GST`
                    : "Incl. GST")}
                </div>
              </div>
              <button onClick={createManual} disabled={pricingLoading || previewPrice.total <= 0} data-testid="manual-save" className="bg-brand-blue text-white py-3 px-6 font-bold uppercase tracking-widest hover:glow-blue disabled:opacity-50">Confirm Booking</button>
            </div>
          </div>
        </div>
      )}

      {chaseTarget && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={() => setChaseTarget(null)} data-testid="chase-modal">
          <div className="bg-[#131A2B] border border-white/12 p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] brand-orange">// PhonePe · Pending</div>
                <h3 className="font-display text-xl uppercase font-black mt-1">Chase Payment</h3>
              </div>
              <button onClick={() => setChaseTarget(null)}><X size={18} /></button>
            </div>
            <div className="text-sm text-white/70 mb-4">
              <b className="text-white">{chaseTarget.user}</b> · {chaseTarget.phone || "no phone on file"}
              <div className="text-xs text-white/50 mt-1">
                Slot booked at {chaseTarget.created_at ? new Date(chaseTarget.created_at).toLocaleTimeString() : "—"}
                {" · "}payment not received yet
              </div>
            </div>
            {!chaseTarget.phone && (
              <div className="text-xs text-red-300 border border-red-500/40 bg-red-500/10 p-3 mb-4">
                No phone number recorded — WhatsApp resend disabled. Cancel the booking or capture the number manually.
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={doChase}
                disabled={chasing || !chaseTarget.phone}
                data-testid="chase-send"
                className="flex-1 bg-brand-blue text-white px-4 py-3 font-bold uppercase tracking-widest text-xs hover:glow-blue disabled:opacity-40"
              >
                {chasing ? "Sending…" : "Resend link on WhatsApp"}
              </button>
              <button
                onClick={() => { cancel(chaseTarget.id); setChaseTarget(null); }}
                data-testid="chase-cancel-booking"
                className="border border-red-500/40 text-red-300 px-4 py-3 font-mono text-xs uppercase tracking-widest hover:bg-red-500/10"
              >
                Cancel booking
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingCheckout && (
        <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4" data-testid="phonepe-qr-modal">
          <div className="bg-[#131A2B] border border-white/12 p-8 max-w-md w-full">
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] brand-orange mb-1">// PhonePe · UPI</div>
                <h3 className="font-display text-2xl uppercase font-black leading-none">
                  {payStatus === "paid" ? "Payment received" : "Scan to pay"}
                </h3>
                <p className="text-xs text-white/60 mt-2">
                  {pendingCheckout.guest} · {formatINR(pendingCheckout.total)}
                </p>
              </div>
              <button onClick={closeCheckout} data-testid="qr-close"><X size={18} /></button>
            </div>

            {payStatus === "paid" ? (
              <div className="text-center py-8" data-testid="qr-paid">
                <CheckCircle2 size={80} className="text-green-400 mx-auto mb-4" />
                <div className="font-display text-lg uppercase font-black text-green-300">Booking confirmed</div>
                <div className="text-xs text-white/50 mt-2">The slot is now blocked on the calendar.</div>
                <button
                  onClick={closeCheckout}
                  className="mt-6 bg-brand-blue text-white px-8 py-3 font-bold uppercase tracking-widest text-sm hover:glow-blue"
                  data-testid="qr-done"
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                <div className="bg-white p-4 flex items-center justify-center" data-testid="qr-canvas">
                  <QRCodeCanvas value={pendingCheckout.url} size={260} level="M" />
                </div>
                <div className="mt-4 flex items-center gap-2 bg-black/40 border border-white/12 px-3 py-2">
                  <QrCode size={14} className="brand-orange shrink-0" />
                  <code className="flex-1 font-mono text-[10px] text-white/70 truncate" title={pendingCheckout.url}>
                    {pendingCheckout.url}
                  </code>
                  <button
                    onClick={() => { navigator.clipboard.writeText(pendingCheckout.url); toast.success("Link copied"); }}
                    className="text-white/60 hover:text-white shrink-0"
                    data-testid="qr-copy"
                    title="Copy link to share"
                  >
                    <Copy size={14} />
                  </button>
                </div>
                <div className="mt-5 flex items-center gap-3 text-xs text-white/60">
                  <div className="h-2 w-2 rounded-full bg-brand-orange animate-pulse" />
                  <span>Waiting for PhonePe confirmation — booking will auto-confirm on payment</span>
                </div>
                <div className="mt-6 flex gap-2">
                  <button
                    onClick={() => window.open(pendingCheckout.url, "_blank", "noopener,noreferrer")}
                    className="flex-1 border border-white/20 text-white px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-white/6"
                    data-testid="qr-open-link"
                  >
                    Open link
                  </button>
                  <button
                    onClick={closeCheckout}
                    className="flex-1 border border-red-500/40 text-red-300 px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-red-500/10"
                    data-testid="qr-cancel"
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {dragRange && dragCursor && dragPreview && (
        <div
          data-testid="drag-preview-chip"
          className={`fixed z-[70] pointer-events-none px-3 py-2 font-mono text-xs uppercase tracking-widest border shadow-lg ${
            dragPreview.blocked
              ? "bg-red-500/20 border-red-500/60 text-red-200"
              : "bg-brand-blue/90 border-brand-blue text-white"
          }`}
          style={{ left: dragCursor.x + 14, top: dragCursor.y + 14 }}
        >
          {dragPreview.blocked ? (
            <span>Blocked — release to cancel</span>
          ) : (
            <span>
              {dragPreview.hours} {dragPreview.hours === 1 ? "hr" : "hrs"}
              {dragPreview.total > 0 && <> · ₹{dragPreview.total.toLocaleString("en-IN")}</>}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, cls = "", testid }) {
  return (
    <div data-testid={testid} className="border border-white/12 bg-[#131A2B] p-4">
      <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-1">{label}</div>
      <div className={`font-display text-3xl font-black ${cls}`}>{value}</div>
    </div>
  );
}
